import { createStore } from 'vuex'


export default createStore({
  state: {
    selectedMode: null,
    players: [],
    currentPlayer: null,
    currentTask: null,
    usedTaskIds: [],
    tasks: [],
    gameHistory: [],
    isLoading: false,
    error: null,
    gameStarted: false,

    // Neue Zustände für erweiterte Aufgabentypen
    activeViruses: [], // Liste der aktiven Virus-Aufgaben mit Spieler und verbleibenden Runden
    activeRules: [],   // Liste der aktiven Regeln mit verbleibenden Runden
    currentRound: 1,   // Aktuelle Rundenzahl
    timerActive: false, // Ist ein Timer aktiv?
    timerDuration: 0,   // Dauer des Timers in Sekunden
    timerRemaining: 0,  // Verbleibende Zeit
    quizAnswerRevealed: false, // Ist die Quiz-Antwort aufgedeckt?

    dissolveMode: false,  // Wenn true, zeigt eine Auflösungsmeldung statt einer Aufgabe an
    dissolveInfo: null,   // Enthält Informationen über die aufzulösende Regel/Virus

    gameModes: [
      {
        id: 'normal',
        title: 'Normal',
        subtitle: 'Klassisches Spiel',
        icon: 'fas fa-glass-cheers'
      },
      {
        id: 'extreme',
        title: 'Extrem',
        subtitle: 'Nur für Mutige',
        icon: 'fas fa-fire'
      },
      {
        id: 'jga',
        title: 'JGA',
        subtitle: 'Junggesellenabschied',
        icon: 'fas fa-crown'
      },
      {
        id: 'hot',
        title: 'Hot',
        subtitle: 'Für heiße Runden',
        icon: 'fas fa-heart'
      },
      {
        id: 'party',
        title: 'Party',
        subtitle: 'Große Gruppe',
        icon: 'fas fa-users'
      },
      {
        id: 'couples',
        title: 'Paare',
        subtitle: 'Für Pärchen',
        icon: 'fas fa-user-friends'
      }
    ]
  },

  getters: {
    getDissolveMode: state => state.dissolveMode,
    getDissolveInfo: state => state.dissolveInfo,
    getSelectedMode: (state) => {
      return state.selectedMode
        ? state.gameModes.find(mode => mode.id === state.selectedMode)
        : null;
    },
    getPlayers: (state) => state.players,
    getGameModes: (state) => state.gameModes,
    canStartGame: (state) => state.players.length >= 2,
    getCurrentPlayer: (state) => state.currentPlayer,
    getCurrentTask: (state) => state.currentTask,
    getGameHistory: (state) => state.gameHistory,
    getRemainingTasks: (state) => {
      return state.tasks.filter(task => !state.usedTaskIds.includes(parseInt(task.id))).length;
    },
    isGameComplete: (state) => {
      // Spiel ist beendet, wenn alle Aufgaben verwendet wurden oder keine Aufgaben vorhanden sind
      return state.tasks.length > 0 && state.usedTaskIds.length >= state.tasks.length;
    },

    // Neue Getter für erweiterte Aufgabentypen
    getActiveViruses: (state) => state.activeViruses,
    getActiveRules: (state) => state.activeRules,
    getCurrentRound: (state) => state.currentRound,
    getTimerActive: (state) => state.timerActive,
    getTimerDuration: (state) => state.timerDuration,
    getTimerRemaining: (state) => state.timerRemaining,
    getQuizAnswerRevealed: (state) => state.quizAnswerRevealed,

    shouldShowPlayer: (state) => {
      if (!state.currentTask) return false;

      // Aufgabentypen, die keinen Spielernamen anzeigen
      const noPlayerTypes = ['group', 'rule'];

      return !noPlayerTypes.includes(state.currentTask.type);
    }
  },

  mutations: {
    setActiveRules(state, rules) {
      state.activeRules = rules;
    },

    setActiveViruses(state, viruses) {
      state.activeViruses = viruses;
    },

    // Mutations für den Auflösungsmodus
    enterDissolveMode(state, dissolveInfo) {
      state.dissolveMode = true;
      state.dissolveInfo = dissolveInfo;
    },

    exitDissolveMode(state) {
      state.dissolveMode = false;
      state.dissolveInfo = null;
    },

    setSelectedMode(state, modeId) {
      state.selectedMode = modeId;
    },

    addPlayer(state, player) {
      state.players.push(player);
    },

    removePlayer(state, index) {
      state.players.splice(index, 1);
    },

    resetPlayers(state) {
      state.players = [];
    },

    setTasks(state, tasks) {
      state.tasks = tasks;
    },

    setCurrentPlayer(state, player) {
      state.currentPlayer = player;
    },

    setCurrentTask(state, task) {
      state.currentTask = task;
      if (task && task.id) {
        state.usedTaskIds.push(parseInt(task.id));
      }

      // Zurücksetzen der Quiz-Antwort
      state.quizAnswerRevealed = false;
    },

    addToGameHistory(state, historyItem) {
      state.gameHistory.push(historyItem);
    },

    resetGame(state) {
      state.currentPlayer = null;
      state.currentTask = null;
      state.usedTaskIds = [];
      state.gameHistory = [];
      state.gameStarted = false;
      state.activeViruses = [];
      state.activeRules = [];
      state.currentRound = 1;
      state.timerActive = false;
      state.timerDuration = 0;
      state.timerRemaining = 0;
      state.quizAnswerRevealed = false;
    },

    startGame(state) {
      state.gameStarted = true;
    },

    setLoading(state, isLoading) {
      state.isLoading = isLoading;
    },

    setError(state, error) {
      state.error = error;
    },

    // Neue Mutations für erweiterte Aufgabentypen

    incrementRound(state) {
      state.currentRound++;

      // Reduziere die verbleibenden Runden für Regeln und Viren
      state.activeRules = state.activeRules.map(rule => {
        return { ...rule, roundsLeft: rule.roundsLeft - 1 };
      }).filter(rule => rule.roundsLeft > 0);

      state.activeViruses = state.activeViruses.map(virus => {
        return { ...virus, roundsLeft: virus.roundsLeft - 1 };
      }).filter(virus => virus.roundsLeft > 0);
    },

    addActiveVirus(state, virus) {
      state.activeViruses.push(virus);
    },

    addActiveRule(state, rule) {
      state.activeRules.push(rule);
    },

    removeActiveVirus(state, virusId) {
      state.activeViruses = state.activeViruses.filter(virus => virus.id !== virusId);
    },

    removeActiveRule(state, ruleId) {
      state.activeRules = state.activeRules.filter(rule => rule.id !== ruleId);
    },

    startTimer(state, duration) {
      state.timerActive = true;
      state.timerDuration = duration;
      state.timerRemaining = duration;
    },

    updateTimer(state, remaining) {
      state.timerRemaining = remaining;
    },

    stopTimer(state) {
      state.timerActive = false;
      state.timerDuration = 0;
      state.timerRemaining = 0;
    },

    setQuizAnswerRevealed(state, revealed) {
      state.quizAnswerRevealed = revealed;
    }
  },

  actions: {
    checkForExpiredRulesAndViruses({ commit, state }) {
      // Prüfen, ob Regeln oder Viren abgelaufen sind

      // Zuerst Regeln prüfen
      if (state.activeRules.length > 0) {
        // Alle Regeln mit reduziertem Zähler aktualisieren
        const updatedRules = state.activeRules.map(rule => ({
          ...rule,
          remainingTasks: rule.remainingTasks - 1
        }));

        // Nach abgelaufenen Regeln suchen
        const expiredRuleIndex = updatedRules.findIndex(rule => rule.remainingTasks <= 0);

        if (expiredRuleIndex >= 0) {
          // Abgelaufene Regel gefunden
          const expiredRule = updatedRules[expiredRuleIndex];

          // Regel aus der Liste entfernen
          const remainingRules = updatedRules.filter((_, index) => index !== expiredRuleIndex);
          commit('setActiveRules', remainingRules);

          // In den Auflösungsmodus wechseln
          commit('enterDissolveMode', {
            type: 'rule',
            rule: expiredRule
          });

          return true; // Es wurde eine Regel aufgelöst
        } else {
          // Aktualisieren der Regeln ohne Auflösung
          commit('setActiveRules', updatedRules);
        }
      }

      // Dann Viren prüfen (nur wenn keine Regel aufgelöst wurde)
      if (state.activeViruses.length > 0) {
        // Alle Viren mit reduziertem Zähler aktualisieren
        const updatedViruses = state.activeViruses.map(virus => ({
          ...virus,
          remainingTasks: virus.remainingTasks - 1
        }));

        // Nach abgelaufenen Viren suchen
        const expiredVirusIndex = updatedViruses.findIndex(virus => virus.remainingTasks <= 0);

        if (expiredVirusIndex >= 0) {
          // Abgelaufener Virus gefunden
          const expiredVirus = updatedViruses[expiredVirusIndex];

          // Virus aus der Liste entfernen
          const remainingViruses = updatedViruses.filter((_, index) => index !== expiredVirusIndex);
          commit('setActiveViruses', remainingViruses);

          // In den Auflösungsmodus wechseln
          commit('enterDissolveMode', {
            type: 'virus',
            virus: expiredVirus
          });

          return true; // Es wurde ein Virus aufgelöst
        } else {
          // Aktualisieren der Viren ohne Auflösung
          commit('setActiveViruses', updatedViruses);
        }
      }

      return false; // Nichts wurde aufgelöst
    },

    // Diese Action wird aufgerufen, wenn der Spieler nach einer Auflösung klickt
    continueAfterDissolve({ commit, dispatch }) {
      // Auflösungsmodus beenden
      commit('exitDissolveMode');

      // Nächste Aufgabe anzeigen
      dispatch('selectRandomPlayerAndTask');
    },

    removeActiveVirus({ commit }, virusId) {
      commit('removeActiveVirus', virusId);
    },

    removeActiveRule({ commit }, ruleId) {
      commit('removeActiveRule', ruleId);
    },

    selectMode({ commit }, modeId) {
      commit('setSelectedMode', modeId);
    },

    addPlayer({ commit }, player) {
      commit('addPlayer', player);
    },

    removePlayer({ commit }, index) {
      commit('removePlayer', index);
    },

    async loadTasks({ commit }) {
      commit('setLoading', true);
      commit('setError', null);

      try {
        const response = await fetch('/tasks.csv');
        const csvData = await response.text();

        // CSV parsen
        const tasks = [];
        const lines = csvData.split('\n');

        if (lines.length === 0) {
          throw new Error('CSV-Datei ist leer');
        }

        // Kopfzeile parsen
        const headers = lines[0].split(',').map(header => header.trim());

        // Prüfen, ob die Kopfzeile gültig ist
        if (headers.length < 3 || !headers.includes('id') || !headers.includes('text')) {
          throw new Error('CSV-Datei hat ungültiges Format');
        }

        // Restliche Zeilen parsen
        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].trim();
          if (!line) continue;

          // Einfache Methode zur Aufteilung der Zeile nach Kommas
          // Im realen Projekt sollte ein robusterer CSV-Parser verwendet werden
          const values = line.split(',');

          // Sicherstellen, dass wir genug Spalten haben
          if (values.length < headers.length) {
            console.warn(`Zeile ${i + 1} hat eine falsche Anzahl an Spalten und wird übersprungen`);
            continue;
          }

          const task = {};

          for (let j = 0; j < headers.length; j++) {
            task[headers[j]] = values[j].trim();
          }

          // Validierung der wichtigsten Felder
          if (!task.id || !task.text || !task.type) {
            console.warn(`Zeile ${i + 1} hat fehlende Pflichtfelder und wird übersprungen`);
            continue;
          }

          // Typ-Konvertierung für numerische Werte
          task.id = parseInt(task.id);
          if (task.difficulty) {
            task.difficulty = parseInt(task.difficulty || '1');
          } else {
            task.difficulty = 1;
          }

          // Duration für Virus und Regel parsen
          if (task.duration) {
            task.duration = parseInt(task.duration);
          }

          tasks.push(task);
        }

        if (tasks.length === 0) {
          throw new Error('Keine gültigen Aufgaben gefunden');
        }

        commit('setTasks', tasks);
      } catch (error) {
        console.error('Fehler beim Laden der Aufgaben:', error);
        commit('setError', 'Aufgaben konnten nicht geladen werden: ' + error.message);
      } finally {
        commit('setLoading', false);
      }
    },

    startGameRound({ commit, state, dispatch }) {
      // Spiellogik initialisieren, wenn das Spiel noch nicht gestartet wurde
      if (!state.gameStarted) {
        commit('startGame');
      }

      dispatch('selectRandomPlayerAndTask');
    },

    selectRandomPlayerAndTask({ commit, state, getters, dispatch }) {
      if (getters.isGameComplete) {
        return;
      }

      // Erhöhe die Rundenzahl
      commit('incrementRound');

      // Sicherstellen, dass Spieler vorhanden sind
      if (state.players.length === 0) {
        commit('setError', 'Keine Spieler vorhanden');
        return;
      }

      // Zufälligen Spieler auswählen
      const randomPlayerIndex = Math.floor(Math.random() * state.players.length);
      const randomPlayer = state.players[randomPlayerIndex];

      // Noch nicht verwendete Aufgaben filtern
      const availableTasks = state.tasks.filter(task => {
        return !state.usedTaskIds.includes(parseInt(task.id));
      });

      if (availableTasks.length > 0) {
        // Zufällige Aufgabe auswählen
        const randomTaskIndex = Math.floor(Math.random() * availableTasks.length);
        const randomTask = availableTasks[randomTaskIndex];

        commit('setCurrentPlayer', randomPlayer);
        commit('setCurrentTask', randomTask);

        // Spezielle Behandlung je nach Aufgabentyp
        dispatch('handleTaskByType', { task: randomTask, player: randomPlayer });

        // Aufgabe zum Spielverlauf hinzufügen
        commit('addToGameHistory', {
          player: randomPlayer,
          task: randomTask,
          round: state.currentRound
        });
      } else if (state.tasks.length > 0) {
        // Alle Aufgaben wurden verwendet
        commit('setCurrentPlayer', null);
        commit('setCurrentTask', null);
      }
    },

    handleTaskByType({ commit, state }, { task, player }) {
      switch (task.type) {
        case 'virus': {
          // Prüfen, ob bereits 2 Regeln/Viren aktiv sind
          if (state.activeViruses.length + state.activeRules.length >= 2) {
            console.log('Bereits 2 Regeln/Viren aktiv, kein neuer Virus hinzugefügt');
            return;
          }

          // Prüfen, ob der Spieler bereits einen Virus hat
          const existingVirus = state.activeViruses.find(v =>
            v.player.name === player.name && v.player.gender === player.gender);

          if (existingVirus) {
            console.log('Spieler hat bereits einen Virus, kein neuer hinzugefügt');
            return;
          }

          if (task.duration) {
            const expiryTasks = 5 + Math.floor(Math.random() * 8);

            commit('addActiveVirus', {
              id: task.id,
              text: task.text,
              player: player,
              roundsLeft: parseInt(task.duration),
              expiryTaskCount: expiryTasks,
              remainingTasks: expiryTasks
            });
          }
          break;
        }

        case 'rule': {
          if (state.activeViruses.length + state.activeRules.length >= 2) {
            console.log('Bereits 2 Regeln/Viren aktiv, keine neue Regel hinzugefügt');
            return;
          }

          if (task.duration) {
            const expiryTasks = 5 + Math.floor(Math.random() * 8);

            commit('addActiveRule', {
              id: task.id,
              text: task.text,
              roundsLeft: parseInt(task.duration),
              expiryTaskCount: expiryTasks,
              remainingTasks: expiryTasks
            });
          }
          break;
        }

        case 'timed': {
          if (task.duration) {
            commit('startTimer', parseInt(task.duration));
          }
          break;
        }
      }
    },


    toggleQuizAnswer({ commit, state }) {
      commit('setQuizAnswerRevealed', !state.quizAnswerRevealed);
    },

    resetTimer({ commit }) {
      commit('stopTimer');
    },

    resetGame({ commit }) {
      commit('resetPlayers');
      commit('setSelectedMode', null);
      commit('resetGame');
    }
  }
})